/*
  Find a Retailer: locator page (sections/far-locator.liquid).

  Reproduces the legacy four lists (doc 04 section 3.1, doc 01d section 1),
  each paged ten at a time, from one Algolia multi-query that fetches every
  list in range in full, so repeat records of one store can be dropped
  across all four before any are shown (QA FAR-004, PwFar.ui.dedupe):

    Destinations  every destination in range, sorted here: currently
                  certified first, then nearest (there are few)
    Certified     nearest first; one query, since every Certified store
                  is certified_current (doc 02 D8)
    Chains        nearest first (shown as National Retailers)
    Unverified    nearest first; only the name is readable

  Every query filters on the visitor's country (no cross-border results).
  Listing status, certification and Connect+ are read from the record and
  never recomputed; nothing sorts by Connect+ tier (doc 04 section 4).

  Needs far-algolia.js, far-location.js and far-ui.js, loaded before this file.
*/
(function () {
  'use strict';

  var PAGE = 10;
  var METRES_PER_MILE = 1609.344;
  var DISTANCES = ['5', '10', '25', '50', '75', '100', '150', '200'];
  // Algolia's most hits per query (paginationLimitedTo), and only the
  // fields a row shows, since every list arrives whole.
  var MAX_HITS = 1000;
  var ATTRIBUTES = [
    'name', 'profile_handle', 'address', 'country', 'phones', 'website', 'programs',
    'list_status', 'list_rank', 'is_destination', 'certified_current', 'certified_employee_count'
  ];

  if (!window.PwFar || !window.PwFar.algolia || !window.PwFar.location || !window.PwFar.ui) return;
  var fill = window.PwFar.ui.fill;
  var el = window.PwFar.ui.el;

  function Locator(root) {
    this.root = root;
    this.config = window.PwFar.config(root);
    this.t = window.PwFar.ui.i18n(root);
    this.form = root.querySelector('[data-far-form]');
    this.postal = this.form.elements.postal;
    this.distance = this.form.elements.distance;
    this.units = this.form.elements.units;
    this.message = root.querySelector('[data-far-message]');
    this.note = root.querySelector('[data-far-note]');
    this.error = root.querySelector('[data-far-error]');
    this.listsNode = root.querySelector('[data-far-lists]');
    this.empty = root.querySelector('[data-far-empty]');
    this.linkProfiles = root.getAttribute('data-link-profiles') === 'true';
    this.lists = {};
    this.searchId = 0;
    this.location = null;

    var self = this;
    ['destination', 'certified', 'chain', 'unverified'].forEach(function (key) {
      var node = root.querySelector('[data-far-list="' + key + '"]');
      self.lists[key] = {
        key: key,
        node: node,
        count: node.querySelector('[data-far-count]'),
        items: node.querySelector('[data-far-items]'),
        more: node.querySelector('[data-far-more]'),
        all: [],
        hits: [],
        total: 0
      };
      self.lists[key].more.addEventListener('click', function () {
        self.more(key);
      });
    });
    // National retailers and Unverified carry no status column (legacy does the same).
    this.lists.chain.node.classList.add('far-list--plain');
    this.lists.unverified.node.classList.add('far-list--plain');

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submit();
    });
    this.distance.addEventListener('change', function () {
      if (self.location) self.search(self.location);
    });
    this.units.addEventListener('change', function () {
      if (self.location) self.search(self.location);
    });
    // Another feature on the page (Get Local) changed the location.
    window.addEventListener(window.PwFar.location.EVENT, function (event) {
      var loc = event.detail;
      if (!loc || (self.location && self.location.postal === loc.postal && self.location.country === loc.country)) return;
      self.postal.value = loc.postal;
      self.search(loc);
    });

    if (!window.PwFar.algolia.ready(this.config)) {
      this.showError(this.t.not_configured);
      return;
    }
    this.start();
  }

  // URL first (shareable searches and legacy deep links), then the
  // visitor's saved location.
  Locator.prototype.start = function () {
    var params = new URLSearchParams(window.location.search);
    var distance = params.get('distance');
    if (DISTANCES.indexOf(distance) !== -1) this.distance.value = distance;
    var units = params.get('units') || params.get('distance_units');
    if (units) this.units.value = /^k/i.test(units) ? 'km' : 'mi';

    var postal = params.get('postal') || params.get('specification');
    if (postal && window.PwFar.location.parse(postal)) {
      this.postal.value = postal;
      this.submit();
      return;
    }

    var lat = parseFloat(params.get('latitude'));
    var lng = parseFloat(params.get('longitude'));
    var country = (params.get('locator_country') || '').toUpperCase();
    if (isFinite(lat) && isFinite(lng) && (country === 'US' || country === 'CA')) {
      this.search({ postal: '', country: country, lat: lat, lng: lng });
      return;
    }

    var saved = window.PwFar.location.load();
    if (saved) {
      this.postal.value = saved.postal;
      this.search(saved);
    }
  };

  Locator.prototype.submit = function () {
    var self = this;
    var text = this.postal.value;
    if (!window.PwFar.location.parse(text)) {
      this.postal.setAttribute('aria-invalid', 'true');
      this.showError(this.t.invalid);
      this.postal.focus();
      return;
    }
    this.postal.removeAttribute('aria-invalid');
    this.hideError();
    this.setMessage(this.t.searching);
    window.PwFar.location.resolve(this.config, text).then(
      function (loc) {
        if (!loc) {
          self.postal.setAttribute('aria-invalid', 'true');
          self.showError(fill(self.t.not_found, { postal: text.trim().toUpperCase() }));
          self.setMessage('');
          return;
        }
        self.postal.value = loc.postal;
        // Search first: it records the location, so the change event that
        // save() fires is recognised as this locator's own and ignored.
        self.search(loc);
        window.PwFar.location.save(loc);
      },
      function () {
        self.showError(self.t.load_error);
        self.setMessage('');
      }
    );
  };

  Locator.prototype.radius = function () {
    var value = parseInt(this.distance.value, 10) || 25;
    return Math.round(this.units.value === 'km' ? value * 1000 : value * METRES_PER_MILE);
  };

  Locator.prototype.request = function (loc, filter, extra) {
    var params = {
      filters: 'country:' + loc.country + ' AND ' + filter,
      aroundLatLng: loc.lat + ',' + loc.lng,
      aroundRadius: this.radius(),
      getRankingInfo: true,
      attributesToHighlight: [],
      attributesToSnippet: [],
      analytics: false
    };
    for (var key in extra) params[key] = extra[key];
    return { indexName: this.config.retailerIndex, params: params };
  };

  var FILTERS = {
    destination: 'list_status:destination',
    certified: 'list_status:certified',
    chain: 'list_status:chain',
    unverified: 'list_status:unverified'
  };

  Locator.prototype.search = function (loc) {
    var self = this;
    var id = ++this.searchId;
    this.location = loc;
    this.hideError();
    this.setMessage(this.t.searching);
    this.listsNode.setAttribute('aria-busy', 'true');

    var all = { hitsPerPage: MAX_HITS, attributesToRetrieve: ATTRIBUTES };
    var requests = [
      this.request(loc, FILTERS.destination, all),
      this.request(loc, FILTERS.certified, all),
      this.request(loc, FILTERS.chain, all),
      this.request(loc, FILTERS.unverified, all)
    ];

    window.PwFar.algolia.search(this.config, requests).then(
      function (results) {
        if (id !== self.searchId) return;
        self.listsNode.setAttribute('aria-busy', 'false');

        // Destinations are ordered here; the others arrive nearest first.
        results[0].hits.sort(function (a, b) {
          var ca = a.certified_current ? 0 : 1;
          var cb = b.certified_current ? 0 : 1;
          if (ca !== cb) return ca - cb;
          return (window.PwFar.algolia.distance(a) || 0) - (window.PwFar.algolia.distance(b) || 0);
        });

        // One record per store across all four lists, before paging.
        var kept = new Set(
          window.PwFar.ui.dedupe([].concat.apply([], results.map(function (result) { return result.hits; })))
        );
        ['destination', 'certified', 'chain', 'unverified'].forEach(function (key, i) {
          var list = self.lists[key];
          var fetched = results[i].hits;
          list.all = fetched.filter(function (hit) { return kept.has(hit); });
          // Past MAX_HITS the count still includes what was not fetched.
          list.total = results[i].nbHits - (fetched.length - list.all.length);
          list.hits = list.all.slice(0, PAGE);
        });

        self.renderAll(loc);
        self.updateUrl(loc);
      },
      function () {
        if (id !== self.searchId) return;
        self.listsNode.setAttribute('aria-busy', 'false');
        self.setMessage('');
        self.showError(self.t.load_error);
      }
    );
  };

  // The next page of one list.
  Locator.prototype.more = function (key) {
    var list = this.lists[key];
    var shown = list.hits.length;
    list.hits = list.all.slice(0, shown + PAGE);
    this.renderList(list, shown);
  };

  Locator.prototype.renderAll = function (loc) {
    var self = this;
    var any = false;
    Object.keys(this.lists).forEach(function (key) {
      var list = self.lists[key];
      self.renderList(list, -1);
      if (list.total > 0) any = true;
    });

    var units = this.units.value;
    var distance = this.distance.value;
    var status = loc.postal
      ? fill(units === 'km' ? this.t.within_km : this.t.within_miles, { distance: distance, postal: loc.postal })
      : '';
    if (loc.approximate) status += ' ' + fill(this.t.approximate, { postal: loc.postal });
    this.setMessage(status);
    this.note.hidden = !any;
    this.empty.hidden = any;
  };

  // from < 0 renders the list afresh; otherwise appends rows from index
  // `from` and moves focus to the first new row.
  Locator.prototype.renderList = function (list, from) {
    var self = this;
    list.node.hidden = list.total === 0;
    list.count.textContent =
      list.total === 1 ? this.t.count_one : fill(this.t.count_other, { count: list.total });

    if (from < 0) {
      list.items.textContent = '';
      from = 0;
    }
    var firstNew = null;
    list.hits.slice(from).forEach(function (hit) {
      var row = self.renderRow(hit, list.key);
      list.items.appendChild(row);
      if (!firstNew) firstNew = row;
    });

    var remaining = list.all.length - list.hits.length;
    list.more.hidden = remaining <= 0;
    list.more.textContent = fill(this.t.show_more, { count: Math.min(PAGE, remaining) });

    if (from > 0 && firstNew) {
      var article = firstNew.querySelector('.far-row');
      article.setAttribute('tabindex', '-1');
      article.focus();
    }
  };

  // Unverified rows: everything but the name goes in blocks that are
  // blurred, hidden from assistive technology and inert, so nothing in them
  // can be read, selected, focused or followed.
  function obscure(node) {
    node.classList.add('far-row__obscured');
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('inert', '');
    return node;
  }

  Locator.prototype.renderRow = function (hit, key) {
    var t = this.t;
    var obscured = key === 'unverified';
    var li = el('li', 'far-list__item');
    var row = el('article', 'far-row');
    li.appendChild(row);

    // An obscured row's links are plain text: no href, nothing to follow.
    function link(text, href, rel) {
      if (obscured) return el('span', null, text);
      var a = el('a', null, text);
      a.href = href;
      if (rel) {
        a.target = '_blank';
        a.rel = rel;
        a.appendChild(el('span', 'far-sr-only', ' ' + t.new_window));
      }
      return a;
    }

    // Location column
    var main = el('div', 'far-row__main');
    var name = el('h3', 'far-row__name');
    if (this.linkProfiles && hit.profile_handle && !obscured) {
      var profile = el('a', null, hit.name);
      profile.href = this.config.profileRoot + encodeURIComponent(hit.profile_handle);
      name.appendChild(profile);
    } else {
      name.textContent = hit.name;
    }
    main.appendChild(name);

    var details = main;
    if (obscured) {
      main.appendChild(el('p', 'far-sr-only', t.details_hidden));
      details = obscure(el('div'));
      main.appendChild(details);
    }

    var distance = window.PwFar.ui.distanceText(window.PwFar.algolia.distance(hit), this.units.value, t);
    if (distance) details.appendChild(el('p', 'far-row__distance', distance));

    var a = hit.address || {};
    var address = el('address', 'far-row__address');
    var lines = [a.street, a.street2, [a.city, [a.province, a.postal].filter(Boolean).join(' ')].filter(Boolean).join(', ')];
    lines.filter(Boolean).forEach(function (line, i) {
      if (i) address.appendChild(document.createElement('br'));
      address.appendChild(document.createTextNode(line));
    });
    details.appendChild(address);

    var links = el('ul', 'far-row__links');
    (hit.phones || []).forEach(function (phone) {
      if (!phone || !phone.display) return;
      var href = window.PwFar.ui.telHref(phone.dial, phone.display);
      if (!href) return;
      var item = el('li');
      item.appendChild(link(phone.display, href));
      if (phone.ext) item.appendChild(document.createTextNode(' ' + fill(t.phone_ext, { ext: phone.ext })));
      links.appendChild(item);
    });
    var website = window.PwFar.ui.safeUrl(hit.website);
    if (website) {
      var siteItem = el('li');
      siteItem.appendChild(link(t.website, website, 'noopener nofollow'));
      links.appendChild(siteItem);
    }
    var destination = window.PwFar.ui.directionsUrl(hit.name, [a.street, a.city, a.province, a.postal, a.country]);
    if (destination) {
      var dirItem = el('li');
      dirItem.appendChild(link(t.directions, destination, 'noopener'));
      links.appendChild(dirItem);
    }
    if (links.children.length) details.appendChild(links);
    row.appendChild(main);

    // Products carried; obscured on Unverified rows, like the rest.
    var programs = hit.programs || [];
    if (programs.length) {
      var column = el('div', 'far-row__programs');
      column.appendChild(el('p', 'far-row__label', t.products_carried));
      var list = el('ul');
      programs.forEach(function (program) {
        list.appendChild(el('li', null, program));
      });
      column.appendChild(list);
      if (obscured) obscure(column);
      row.appendChild(column);
    }

    // Status: Destinations and Certified only
    if (key === 'destination' || key === 'certified') {
      var status = el('div', 'far-row__status');
      var badges = el('ul', 'far-row__badges');
      if (hit.is_destination || hit.list_status === 'destination') {
        badges.appendChild(el('li', 'far-badge', t.badge_destination));
      }
      if (hit.certified_current) badges.appendChild(el('li', 'far-badge', t.badge_certified));
      if (badges.children.length) status.appendChild(badges);
      var employees = parseInt(hit.certified_employee_count, 10);
      if (employees > 0) {
        status.appendChild(
          el('p', 'far-row__employees', employees === 1 ? t.employees_one : fill(t.employees_other, { count: employees }))
        );
      }
      if (status.children.length) row.appendChild(status);
    }

    return li;
  };

  Locator.prototype.updateUrl = function (loc) {
    if (!loc.postal || !window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    ['specification', 'distance_units', 'latitude', 'longitude', 'locator_country'].forEach(function (key) {
      params.delete(key);
    });
    params.set('postal', loc.postal);
    params.set('distance', this.distance.value);
    params.set('units', this.units.value);
    window.history.replaceState(window.history.state, '', window.location.pathname + '?' + params.toString());
  };

  Locator.prototype.setMessage = function (text) {
    this.message.textContent = text || '';
  };

  Locator.prototype.showError = function (text) {
    this.error.textContent = text || '';
    this.error.hidden = !text;
  };

  Locator.prototype.hideError = function () {
    this.showError('');
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-far-locator]').forEach(function (root) {
      if (root.farLocator) return;
      root.farLocator = new Locator(root);
    });
  }

  init(document);

  // Theme editor: re-initialise a section when it is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
